import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DirectedGraphEngine, { NodeId, Edge } from "../GraphEngine/DirectedGraphEngine";
import styles from "../Graph.module.css";


type TraversalType = "bfs" | "dfs" | null;

const ALPHABET: NodeId[] = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const DirectedGraphWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [nodes, setNodes] = useState<NodeId[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [traversal, setTraversal] = useState<TraversalType>(null);
  const [startNode, setStartNode] = useState<NodeId | null>(null);
  const [resetSignal, setResetSignal] = useState(0);
  const [traversalResult, setTraversalResult] = useState<NodeId[]>([]);
  const [highlightedNodes, setHighlightedNodes] = useState<NodeId[]>([]);
  const [edgeFrom, setEdgeFrom] = useState<NodeId | "">("");
  const [edgeTo, setEdgeTo] = useState<NodeId | "">("");
  const [nextNodeIndex, setNextNodeIndex] = useState<number>(0);
  const [showBFSPseudo, setShowBFSPseudo] = useState(false);
  const [showDFSPseudo, setShowDFSPseudo] = useState(false);

  function generateMostlyBidirectionalGraph(nodes: NodeId[]): Edge[] {
  const edges: Edge[] = [];

  for (let i = 0; i < nodes.length; i++) {
    const a = nodes[i];
    const b = nodes[(i + 1) % nodes.length];

    edges.push({ from: a, to: b });
    edges.push({ from: b, to: a });
  }

  const fromIndex = Math.floor(Math.random() * nodes.length);
  let toIndex = Math.floor(Math.random() * nodes.length);

  while (toIndex === fromIndex) {
    toIndex = Math.floor(Math.random() * nodes.length);
  }

  edges.push({
    from: nodes[fromIndex],
    to: nodes[toIndex],
  });

  return edges;
}

  useEffect(() => {
    if (nodes.length === 0) {
      setStartNode(null);
      setEdgeFrom("");
      setEdgeTo("");
      return;
    }
    setStartNode((prev) => (prev && nodes.includes(prev) ? prev : nodes[0]));
    setEdgeFrom((prev) => (prev && nodes.includes(prev) ? prev : nodes[0]));
    setEdgeTo((prev) =>
      prev && nodes.includes(prev)
        ? prev
        : nodes.length > 1
        ? nodes[1]
        : nodes[0]
    );
  }, [nodes]);

  const handleTraversalResult = useCallback(
    (result: React.SetStateAction<NodeId[]>) => setTraversalResult(result),
    []
  );

  const handleAddNode = () => {
    if (nextNodeIndex >= ALPHABET.length) return;
    const newNode = ALPHABET[nextNodeIndex];
    setNodes((prev) => [...prev, newNode]);
    setNextNodeIndex((i) => i + 1);
    setTraversal(null);
    setHighlightedNodes([]);
    setTraversalResult([]);
    setResetSignal((s) => s + 1);
  };

  const handleAddEdge = () => {
    if (!edgeFrom || !edgeTo) return;
    if (edgeFrom === edgeTo) return;

    const exists = edges.some(
      (e) => e.from === edgeFrom && e.to === edgeTo
    );
    if (exists) return;

    setEdges((prev) => {
    const newEdges = [...prev];

    if (!newEdges.some(e => e.from === edgeFrom && e.to === edgeTo)) {
        newEdges.push({ from: edgeFrom, to: edgeTo });
    }

    if (!newEdges.some(e => e.from === edgeTo && e.to === edgeFrom)) {
        newEdges.push({ from: edgeTo, to: edgeFrom });
    }
    return newEdges;
    });
  };

  const handleGenerateGraph = () => {
    const baseNodes = ALPHABET.slice(0, 5);
    const possible: Edge[] = [];
    for (let i = 0; i < baseNodes.length; i++) {
      for (let j = 0; j < baseNodes.length; j++) {
        if (i !== j) possible.push({ from: baseNodes[i], to: baseNodes[j] });
      }
    }

    const shuffled = [...possible].sort(() => Math.random() - 0.5);
    const edgeCount = 5 + Math.floor(Math.random() * 4); // 5–8 edges
    const randomEdges = shuffled.slice(0, edgeCount);
    setNodes(baseNodes);
    setEdges(randomEdges);
    setNextNodeIndex(5);
    setTraversal(null);
    setHighlightedNodes([]);
    setTraversalResult([]);
    setResetSignal((s) => s + 1);
  };

  const handleClearGraph = () => {
    setNodes([]);
    setEdges([]);
    setTraversal(null);
    setTraversalResult([]);
    setHighlightedNodes([]);
    setResetSignal((s) => s + 1);
    setNextNodeIndex(0);
  };

  const runTraversal = (type: TraversalType) => {
    if (!startNode || nodes.length === 0) return;
    setTraversal(null);
    setResetSignal((s) => s + 1);
    setTimeout(() => {
        setHighlightedNodes([]);
        setTraversalResult([]);
        setTraversal(type);
    }, 0);
  };

  const handleResetHighlighting = () => {
    setTraversal(null);
    setHighlightedNodes([]);
    setTraversalResult([]);
    setResetSignal((s) => s + 1);
  };

  const adjacencyList = useMemo(() => {
    const adj: Record<NodeId, NodeId[]> = {};
    nodes.forEach((n) => (adj[n] = []));
    edges.forEach(({ from, to }) => adj[from].push(to));
    return adj;
  }, [nodes, edges]);

  const adjacencyMatrix = useMemo(() => {
    const index: Record<NodeId, number> = {};
    nodes.forEach((n, i) => (index[n] = i));
    const matrix = Array.from({ length: nodes.length }, () =>
      Array(nodes.length).fill(0)
    );

    edges.forEach(({ from, to }) => {
      matrix[index[from]][index[to]] = 1;
    });

    return matrix;
  }, [nodes, edges]);

  const unreachableNodes = useMemo(() => {
    if (!startNode) return [];
    return nodes.filter((n) => !traversalResult.includes(n));
  }, [nodes, traversalResult, startNode]);

  return (
    <div className={styles.graphPageContainer}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>

      <h1 className={styles.title}>Directed Graph</h1>
      <p className={styles.description}>
        This page lets you build your own <strong>directed graph</strong>.
        Nodes are labeled A, B, C… and edges have direction
        (<strong>A → B</strong> is not the same as <strong>B → A</strong>).
        We draw the graph in a <strong>circular layout</strong> to keep
        the structure readable as it grows.
      </p>

      <div className={styles.useCase}>
        <h3>Use Cases</h3>
        <p>
          Directed graphs are used for <strong>web links</strong>,{" "}
          <strong>follower networks</strong>,{" "}
          <strong>workflows and state machines</strong>, and{" "}
          <strong>dependency graphs</strong>.
        </p>
      </div>

      <div className={styles.timeComplexity}>
        <h3>Time Complexity</h3>
        <p>
          BFS and DFS both run in <strong>O(V + E)</strong>, where V is the
          number of nodes and E is the number of directed edges.
        </p>
      </div>

      <div className={styles.graphControlsRow}>

        <div className={styles.graphControlsGroup}>
          <h3>Build Graph</h3>

          <button onClick={handleAddNode}>Add Node</button>
          <button onClick={handleGenerateGraph}>Generate Graph</button>
          <button onClick={handleClearGraph}>Clear Graph</button>

          <p className={styles.controlsNote}>
            Add nodes manually or generate a directed graph.
            Generated graphs may include{" "}
            <strong>unreachable nodes</strong>.
          </p>

          <h4 style={{ marginTop: "0.75rem" }}>Add Edge</h4>

          {nodes.length < 2 ? (
            <p className={styles.emptyState}>
              Add at least two nodes to create edges.
            </p>
          ) : (
            <div className={styles.inlineForm}>
              <label>
                From:
                <select
                  value={edgeFrom}
                  onChange={(e) =>
                    setEdgeFrom(e.target.value as NodeId)
                  }
                >
                  {nodes.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                To:
                <select
                  value={edgeTo}
                  onChange={(e) =>
                    setEdgeTo(e.target.value as NodeId)
                  }
                >
                  {nodes.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>

              <button onClick={handleAddEdge}>Add Edge</button>
            </div>
          )}
        </div>

        <div className={styles.graphControlsGroup}>
          <h3>Traversal</h3>

          <div className={styles.pseudocodeContainer}>
            <button
              className={styles.pseudocodeToggle}
              onClick={() => setShowBFSPseudo((s) => !s)}
            >
              {showBFSPseudo ? "▼ Hide BFS Pseudocode" : "▶ Show BFS Pseudocode"}
            </button>

            {showBFSPseudo && (
              <pre className={styles.pseudocodeBlock}>
                {`BFS(start):
                  queue ← [start]
                  visited ← {start}

                  while queue not empty:
                      node ← queue.pop_front()
                      visit(node)
                      for each OUTGOING neighbor in adjacency[node]:
                          if neighbor not in visited:
                              visited.add(neighbor)
                              queue.push(neighbor)`}
              </pre>
            )}

            <button
              className={styles.pseudocodeToggle}
              onClick={() => setShowDFSPseudo((s) => !s)}
            >
              {showDFSPseudo ? "▼ Hide DFS Pseudocode" : "▶ Show DFS Pseudocode"}
            </button>

            {showDFSPseudo && (
              <pre className={styles.pseudocodeBlock}>
                {`DFS(node):
                  visited.add(node)
                  visit(node)
                  for each OUTGOING neighbor in adjacency[node]:
                      if neighbor not in visited:
                          DFS(neighbor)`}
              </pre>
            )}
          </div>

          {nodes.length === 0 ? (
            <p className={styles.emptyState}>
              Add nodes or generate a graph to run BFS or DFS.
            </p>
          ) : (
            <>
              <div className={styles.inlineForm}>
                <label>
                  Start Node:
                  <select
                    value={startNode ?? ""}
                    onChange={(e) =>
                      setStartNode(e.target.value as NodeId)
                    }
                  >
                    {nodes.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <button onClick={() => runTraversal("bfs")}>Run BFS</button>
              <button onClick={() => runTraversal("dfs")}>Run DFS</button>
              <button onClick={handleResetHighlighting}>Reset Highlighting</button>

              <p className={styles.controlsNote}>
                <strong>Note:</strong> BFS and DFS follow only{" "}
                <strong>outgoing edges</strong> in a directed graph.
                The visual layout does not affect traversal — the{" "}
                <strong>adjacency list</strong> controls exploration order.
              </p>
            </>
          )}

          <p className={styles.traversalResult}>
            Traversal result:{" "}
            {traversalResult.length > 0
              ? traversalResult.join(" → ")
              : "No traversal run yet."}
          </p>

          {startNode && nodes.length > 0 && (
            <p className={styles.traversalResult}>
              Unreachable nodes:{" "}
              {unreachableNodes.length
                ? unreachableNodes.join(", ")
                : "None — all nodes reachable."}
            </p>
          )}
        </div>
      </div>

      <div className={styles.graphCanvasCard}>
        <DirectedGraphEngine
          nodes={nodes}
          edges={edges}
          traversal={traversal}
          startNode={startNode}
          resetSignal={resetSignal}
          highlightedNodes={highlightedNodes}
          setHighlightedNodes={setHighlightedNodes}
          setTraversalResult={handleTraversalResult}
        />
      </div>

      <div className={styles.graphRepRow}>
        <div className={styles.graphRepCard}>
          <h3>Adjacency List</h3>
          {nodes.length === 0 ? (
            <p className={styles.emptyState}>
              No nodes yet. Add nodes or generate a graph to see the adjacency list.
            </p>
          ) : (
            <pre className={styles.codeBlock}>
              {nodes
                .map((n) => {
                  const neighbors = adjacencyList[n];
                  return `${n}: ${
                    neighbors.length
                      ? neighbors.map((m) => `→ ${m}`).join(", ")
                      : "—"
                  }`;
                })
                .join("\n")}
            </pre>
          )}
        </div>

        <div className={styles.graphRepCard}>
          <h3>Adjacency Matrix</h3>
          {nodes.length === 0 ? (
            <p className={styles.emptyState}>
              No nodes yet. The adjacency matrix will appear once the graph has nodes and edges.
            </p>
          ) : (
            <table className={styles.matrixTable}>
              <thead>
                <tr>
                  <th></th>
                  {nodes.map((n) => (
                    <th key={n}>{n}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {nodes.map((rowNode, rowIndex) => (
                  <tr key={rowNode}>
                    <th>{rowNode}</th>
                    {nodes.map((_, colIndex) => (
                      <td key={colIndex}>
                        {adjacencyMatrix[rowIndex][colIndex]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default DirectedGraphWrapper;

